import type { AuthUser } from "@/contexts/AuthContext";

export const ROLES = {
    SA: "sa",
    COMPANY: "facility.admin",
    COMPANY_INVITED: "invited",
    COMPANY_ADMIN: "company.admin",
    COMPANY_EMPLOYEE: "employee",
    PARKING_CONTROLLER: "parking.operator",
} as const;

export function hasAnyRole(
    user: Pick<AuthUser, "role"> | null,
    roles: readonly string[]
): boolean {
    const role = user?.role;
    return typeof role === "string" && roles.includes(role);
}

export function canAccessPayments(user: Pick<AuthUser, "role"> | null): boolean {
    return hasAnyRole(user, [
        ROLES.COMPANY,
        ROLES.PARKING_CONTROLLER,
        ROLES.SA,
    ]);
}

export function canAccessParkingPermits(user: Pick<AuthUser, "role"> | null): boolean {
    return hasAnyRole(user, [
        ROLES.COMPANY,
        ROLES.PARKING_CONTROLLER,
        ROLES.SA,
    ]);
}

export function canAccessWhitelist(user: Pick<AuthUser, "role"> | null): boolean {
    return hasAnyRole(user, [
        ROLES.COMPANY,
        ROLES.PARKING_CONTROLLER,
        ROLES.SA,
    ]);
}

export function canAccessBlacklist(user: Pick<AuthUser, "role"> | null): boolean {
    return hasAnyRole(user, [
        ROLES.COMPANY,
        ROLES.PARKING_CONTROLLER,
        ROLES.SA,
    ]);
}

export function canAccessKiosks(user: Pick<AuthUser, "role"> | null): boolean {
    return hasAnyRole(user, [
        ROLES.COMPANY,
        ROLES.SA,
        ROLES.PARKING_CONTROLLER,
    ]);
}

export function canAccessTurnover(user: Pick<AuthUser, "role"> | null): boolean {
    return hasAnyRole(user, [
        ROLES.COMPANY,
        ROLES.PARKING_CONTROLLER,
        ROLES.SA,
    ]);
}

export function canAccessSummary(user: Pick<AuthUser, "role"> | null): boolean {
    return hasAnyRole(user, [
        ROLES.PARKING_CONTROLLER,
        ROLES.SA,
    ]);
}

export function canAccessEmployees(user: Pick<AuthUser, "role"> | null): boolean {
    return hasAnyRole(user, [
        ROLES.COMPANY_ADMIN,
        ROLES.SA,
    ]);
}

export function canAccessCompanyPayments(user: Pick<AuthUser, "role"> | null): boolean {
    return hasAnyRole(user, [
        ROLES.SA,
    ]);
}

export function canAccessBillingReport(user: Pick<AuthUser, "role"> | null): boolean {
    return hasAnyRole(user, [
        ROLES.SA,
    ]);
}

export function canAccessDashboard(user: Pick<AuthUser, "role"> | null): boolean {
    return hasAnyRole(user, [
        ROLES.COMPANY,
        ROLES.COMPANY_ADMIN,
        ROLES.PARKING_CONTROLLER,
        ROLES.SA,
    ]);
}

export function canAccessBusinessOverview(
    user: Pick<AuthUser, "role"> | null,
): boolean {
    return hasAnyRole(user, [
        ROLES.COMPANY,
        ROLES.PARKING_CONTROLLER,
        ROLES.SA,
    ]);
}

export function getHomeRoute(user: Pick<AuthUser, "role"> | null): string {
    return canAccessDashboard(user) ? "/dashboard" : "/vehicles";
}

/** API flag: 1 / "1" / true is company admin; 0 / "0" / false is employee. */
export function isCompanyAdminValue(value: unknown): boolean {
    return (
        value === 1 ||
        value === true ||
        value === "1" ||
        String(value).toLowerCase() === "true"
    );
}

export function getRoleLabelKey(
    role: string | null | undefined,
): string | null {
    switch (role) {
        case ROLES.SA:
            return "roles.sa";
        case ROLES.COMPANY:
            return "roles.facilityAdmin";
        case ROLES.COMPANY_INVITED:
            return "roles.invited";
        case ROLES.COMPANY_ADMIN:
            return "roles.companyAdmin";
        case ROLES.COMPANY_EMPLOYEE:
            return "roles.employee";
        case ROLES.PARKING_CONTROLLER:
            return "roles.parkingOperator";
        default:
            return null;
    }
}
